<script setup>
import { computed, ref, watch } from 'vue';
import { Eye, EyeOff, Check, AlertCircle, UserRound, Database } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import MobileShell from '../../components/MobileShell.vue';
import LanguageSwitch from '../../components/LanguageSwitch.vue';
import { api } from '../../services/api';
import { monitorDeviceIdChanges, resolveDeviceId, saveDeviceId } from '../../services/device-id';
import { cachePlatformReturnUrl } from '../../services/platform-return';
import { state } from '../../data/store';

const props = defineProps({ mode: { type: String, default: 'login' } });
const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const isLogin = computed(() => props.mode === 'login');
const email = ref('');
const password = ref('');
const name = ref('');
const confirmPassword = ref('');
const remember = ref(true);
const showPassword = ref(false);
const loading = ref(false);
const error = ref('');

watch(() => props.mode, () => { error.value = ''; });

function saveProfile(profile) {
  localStorage.setItem('u1card-profile', JSON.stringify(profile));
  if (profile.deviceId) saveDeviceId(profile.deviceId, 'account');
  Object.assign(state.profile, {
    name: profile.name || profile.email.split('@')[0],
    email: profile.email,
    status: profile.status || 'active',
    deviceId: profile.deviceId || ''
  });
}

async function submit() {
  loading.value = true;
  error.value = '';
  try {
    if (!isLogin.value && password.value !== confirmPassword.value) throw new Error(t('auth.passwordMismatch'));
    const resolvedDevice = await resolveDeviceId(locale.value);
    const result = isLogin.value
      ? await api.login(email.value, password.value, locale.value, resolvedDevice.deviceId)
      : await api.register(email.value, password.value, name.value, locale.value, resolvedDevice.deviceId);
    localStorage.setItem('u1card-session-token', result.token);
    saveProfile(result.profile);
    if (typeof route.query.platformTicket === 'string' && route.query.platformTicket) {
      const binding = await api.bindPlatform(route.query.platformTicket);
      cachePlatformReturnUrl(binding.returnUrl);
    }
    monitorDeviceIdChanges(resolvedDevice.deviceId, async (deviceId) => {
      const synced = await api.syncDeviceId(deviceId, locale.value);
      if (synced.profile) saveProfile(synced.profile);
    });
    const redirect = typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/') ? route.query.redirect : '/home';
    router.push(redirect);
  } catch (err) {
    error.value = err.message || t('auth.passwordError');
  } finally { loading.value = false; }
}
</script>

<template>
  <MobileShell :nav="false">
    <div class="auth-language"><LanguageSwitch /></div>
    <div class="auth-brand">
      <img src="/assets/u1card-logo.png" alt="U1Card" />
    </div>
    <h1>{{ isLogin ? t('auth.loginTitle') : t('auth.registerTitle') }}</h1>
    <div class="auth-api-note"><Database :size="13" /><span>{{ t('auth.liveAccount') }}</span></div>
    <form class="auth-form" @submit.prevent="submit">
      <label v-if="!isLogin" class="field-group">
        <span class="field-label">{{ t('auth.name') }}</span>
        <span class="field-shell"><UserRound class="leading" :size="19" /><input v-model="name" class="field" type="text" autocomplete="name" :placeholder="t('auth.nameHint')" /></span>
      </label>
      <label class="field-group">
        <span class="field-label">{{ t('auth.email') }}</span>
        <input v-model="email" class="field" type="email" autocomplete="email" placeholder="name@example.com" required />
      </label>
      <label class="field-group">
        <span class="field-label">{{ isLogin ? t('auth.password') : t('auth.setPassword') }}</span>
        <span class="field-shell password-shell">
          <input v-model="password" class="field" :class="{ error }" :type="showPassword ? 'text' : 'password'" :autocomplete="isLogin ? 'current-password' : 'new-password'" minlength="6" required @input="error = ''" />
          <button class="trailing icon-button" type="button" @click="showPassword = !showPassword"><Eye v-if="showPassword" :size="20" /><EyeOff v-else :size="20" /></button>
        </span>
        <span v-if="error" class="field-error"><AlertCircle :size="13" /> {{ error }}</span>
      </label>
      <template v-if="!isLogin">
        <label class="field-group">
          <span class="field-label">{{ t('auth.confirmPassword') }}</span>
          <input v-model="confirmPassword" class="field" type="password" autocomplete="new-password" minlength="6" required />
        </label>
        <label class="agreement"><button class="checkbox checked" type="button"><Check :size="16" /></button><span>{{ t('auth.agree') }}</span></label>
      </template>
      <div v-else class="login-options">
        <label><button class="checkbox" :class="{ checked: remember }" type="button" @click="remember = !remember"><Check v-if="remember" :size="16" /></button><span>{{ t('auth.remember') }}</span></label>
        <button class="text-button" type="button">{{ t('auth.forgot') }}</button>
      </div>
      <button class="primary-button" :disabled="loading || !email || !password" type="submit">{{ loading ? t('common.loading') : (isLogin ? t('auth.login') : t('auth.register')) }}</button>
      <p class="auth-switch"><span>{{ isLogin ? t('auth.noAccount') : t('auth.hasAccount') }}</span> <RouterLink :to="isLogin ? '/register' : '/login'">{{ isLogin ? t('auth.apply') : t('auth.goLogin') }}</RouterLink></p>
    </form>
  </MobileShell>
</template>

<style scoped>
.auth-language { position: absolute; z-index: 25; top: calc(18px + env(safe-area-inset-top, 0px)); right: 20px; }
.auth-brand { width: 108px; height: 132px; margin: 34px auto 0; display: grid; place-items: center; filter: drop-shadow(0 10px 24px rgba(53,74,178,.42)); }
.auth-brand img { width: 116px; height: 116px; object-fit: contain; }
h1 { margin: 0 0 10px; text-align: center; font-size: 23px; font-weight: 720; letter-spacing: -.025em; }
.auth-api-note { width:max-content; max-width:100%; margin:0 auto 22px; padding:6px 10px; display:flex; align-items:center; gap:6px; border:1px solid rgba(91,238,161,.2); border-radius:9px; background:rgba(54,205,125,.07); color:#72dda5; font-size:10px; font-weight:600; }
.auth-form { position: relative; z-index: 2; }
.field-group { display: block; margin-bottom: 17px; }
.field-label { margin-bottom: 7px; }
.password-shell .field { padding-left: 16px; }
.icon-button { display: grid; place-items: center; padding: 0; border: 0; background: none; cursor: pointer; }
.field-error { display: flex; align-items: center; gap: 4px; }
.login-options { display: flex; justify-content: space-between; align-items: center; margin: -1px 2px 20px; }
.login-options label, .agreement { display: flex; align-items: center; gap: 8px; color: #aaa; font-size: 12px; }
.checkbox { width: 19px; height: 19px; padding: 0; border: 1px solid #5c5c65; border-radius: 6px; background: #19191c; display: grid; place-items: center; cursor: pointer; }
.checkbox.checked { border-color: #f3f3f4; color: #111; background: #f3f3f4; }
.agreement { margin: -4px 1px 17px; color: #d1d0d4; }
.auth-switch { margin: 20px 0 0; text-align: center; color: #75747b; font-size: 12px; }
.auth-switch a { color: #fff; font-weight: 750; }
</style>
